import moment from 'moment';

import type { WorkJourneyWorkShift } from '@src/packages/workJourney/types';

export type ReplacementJourneyImpact =
  | {
      kind: 'no_impact';
      userDisplayName: string;
      currentJourneyName: string | null;
    }
  | {
      kind: 'impact';
      userDisplayName: string;
      currentJourneyName: string;
      remainingMembers: number;
      requiredMembers: number;
    };

type DaySchedule = {
  isActive?: boolean;
  startTime?: string | null;
  endTime?: string | null;
};

type WorkShiftLike = {
  id?: string;
  daysOfWeek?: Array<string | number>;
  recurrence?: {
    weeks?: Array<{
      days?: Record<string, DaySchedule>;
    }>;
  };
} | null | undefined;

const MINUTES_PER_DAY = 24 * 60;

/** Parses `HH:mm` / `HH:mm:ss` into minutes from midnight. Returns null if invalid. */
export const parseTimeToMinutes = (value: string | null | undefined): number | null => {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/**
 * Expands a possibly overnight range into one or two [start, end) segments in [0, 1440).
 */
export const expandTimeRangeToSegments = (
  startMinutes: number,
  endMinutes: number,
): Array<{ start: number; end: number }> => {
  if (startMinutes === endMinutes) {
    return [{ start: 0, end: MINUTES_PER_DAY }];
  }
  if (endMinutes > startMinutes) {
    return [{ start: startMinutes, end: endMinutes }];
  }
  // Overnight: e.g. 22:00 → 06:00
  return [
    { start: startMinutes, end: MINUTES_PER_DAY },
    { start: 0, end: endMinutes },
  ];
};

export const doTimeRangesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean => {
  const segmentsA = expandTimeRangeToSegments(startA, endA);
  const segmentsB = expandTimeRangeToSegments(startB, endB);

  return segmentsA.some(a =>
    segmentsB.some(b => a.start < b.end && b.start < a.end),
  );
};

const resolveDaySchedule = (
  workShift: WorkShiftLike,
  isoWeekday: number,
): DaySchedule | null => {
  if (!workShift) return null;

  const weeks = workShift.recurrence?.weeks ?? [];
  for (const week of weeks) {
    const days = week.days ?? {};
    const byNumber = days[String(isoWeekday)] ?? days[isoWeekday as unknown as string];
    if (byNumber) return byNumber;
  }

  // Fallback: active if daysOfWeek includes the ISO weekday, without per-day times.
  if (Array.isArray(workShift.daysOfWeek) && workShift.daysOfWeek.includes(isoWeekday)) {
    return { isActive: true };
  }

  return null;
};

/**
 * Whether two work shifts overlap on a calendar date (same ISO weekday + overlapping hours).
 * Overnight ranges are supported. Missing schedules → no overlap (cannot prove conflict).
 */
export const doWorkShiftsOverlapOnDate = (
  coverShift: WorkShiftLike,
  currentShift: WorkShiftLike,
  date: string,
): boolean => {
  if (!coverShift || !currentShift) return false;

  const day = moment(date, 'YYYY-MM-DD', true);
  if (!day.isValid()) return false;
  const isoWeekday = day.isoWeekday(); // 1=Mon … 7=Sun

  const coverDay = resolveDaySchedule(coverShift, isoWeekday);
  const currentDay = resolveDaySchedule(currentShift, isoWeekday);

  if (!coverDay || coverDay.isActive === false) return false;
  if (!currentDay || currentDay.isActive === false) return false;

  const coverStart = parseTimeToMinutes(coverDay.startTime);
  const coverEnd = parseTimeToMinutes(coverDay.endTime);
  const currentStart = parseTimeToMinutes(currentDay.startTime);
  const currentEnd = parseTimeToMinutes(currentDay.endTime);

  // Same day active but no times → treat as full-day overlap for safety.
  if (
    coverStart == null ||
    coverEnd == null ||
    currentStart == null ||
    currentEnd == null
  ) {
    return true;
  }

  return doTimeRangesOverlap(coverStart, coverEnd, currentStart, currentEnd);
};

export type EvaluateReplacementJourneyImpactParams = {
  userDisplayName: string;
  coverDate: string;
  coverWorkShift: WorkJourneyWorkShift | WorkShiftLike | null;
  currentWorkJourney: {
    workJourneyConfigId: string;
    workJourneyConfigName: string | null;
  } | null;
  currentWorkShift: WorkJourneyWorkShift | WorkShiftLike | null;
  currentRequiredMembers: number;
  currentAssignedMembers: number;
};

/**
 * Decides which confirm modal to show when selecting a replacement candidate.
 */
export const evaluateReplacementJourneyImpact = (
  params: EvaluateReplacementJourneyImpactParams,
): ReplacementJourneyImpact => {
  const {
    userDisplayName,
    coverDate,
    coverWorkShift,
    currentWorkJourney,
    currentWorkShift,
    currentRequiredMembers,
    currentAssignedMembers,
  } = params;

  if (!currentWorkJourney) {
    return {
      kind: 'no_impact',
      userDisplayName,
      currentJourneyName: null,
    };
  }

  const overlaps = doWorkShiftsOverlapOnDate(
    coverWorkShift,
    currentWorkShift,
    coverDate,
  );

  if (!overlaps) {
    return {
      kind: 'no_impact',
      userDisplayName,
      currentJourneyName: currentWorkJourney.workJourneyConfigName,
    };
  }

  const requiredMembers = Math.max(0, currentRequiredMembers);
  const remainingMembers = Math.max(0, currentAssignedMembers - 1);

  return {
    kind: 'impact',
    userDisplayName,
    currentJourneyName:
      currentWorkJourney.workJourneyConfigName || currentWorkJourney.workJourneyConfigId,
    remainingMembers,
    requiredMembers,
  };
};
